// Spherical-gravity character controller. Walks on the planet surface,
// keeps "feet" at sampleRadius(dir) + eyeHeight, and exposes a smooth
// camera basis (forward/right/up) where up = surface normal.

import * as THREE from 'three';

export class Walker {
  constructor({ planet, eyeHeight = 1.7, walkSpeed = 4.5, sprintSpeed = 8.5, crouchSpeed = 2.0, radius = 0.4 }) {
    this.planet = planet;
    this.eyeHeight = eyeHeight;
    this.walkSpeed = walkSpeed;
    this.sprintSpeed = sprintSpeed;
    this.crouchSpeed = crouchSpeed;
    this.radius = radius;
    // Spherical coords on planet
    this.dir = new THREE.Vector3(0, 1, 0); // current up direction (unit)
    this.pitch = 0; // first-person camera pitch only
    // Output state
    this.position = new THREE.Vector3();
    this.up = new THREE.Vector3(0, 1, 0);
    this.forward = new THREE.Vector3(0, 0, -1);
    this.right = new THREE.Vector3(1, 0, 0);
    this.crouching = false;
    this.sprinting = false;
    this.moving = false;
    this.stamina = 1.0;
    // For footstep events
    this.distanceTraveled = 0;
    this.lastFootstepDistance = 0;
    this._tmp = { v: new THREE.Vector3(), q: new THREE.Quaternion(), m: new THREE.Matrix4() };
  }

  // Set spawn at (lon, lat) on the planet
  spawnAt(lon, lat) {
    this.dir.set(
      Math.cos(lat) * Math.cos(lon),
      Math.sin(lat),
      Math.cos(lat) * Math.sin(lon)
    ).normalize();
    this._sync();
  }

  _sync() {
    // Surface position with eye/crouch height. Basis is maintained in update();
    // this just keeps `up` and `position` in sync with `dir`.
    this.up.copy(this.dir);
    const r = this.planet.sampleRadius(this.dir);
    const h = this.crouching ? this.eyeHeight * 0.55 : this.eyeHeight;
    this.position.copy(this.dir).multiplyScalar(r + h);
    // Re-orthonormalize against the (possibly drifted) up; preserves heading.
    this.right.crossVectors(this.forward, this.up);
    if (this.right.lengthSq() < 1e-8) {
      // Degenerate (forward parallel to up): pick any tangent.
      const ref = Math.abs(this.up.y) > 0.95 ? new THREE.Vector3(0, 0, 1) : new THREE.Vector3(0, 1, 0);
      this.right.crossVectors(ref, this.up);
    }
    this.right.normalize();
    this.forward.crossVectors(this.up, this.right).normalize();
  }

  // input: { forward, right, sprint, crouch, mouseDX, mouseDY }
  update(input, dt, props = []) {
    // Mouse look — pitch is for first-person camera
    this.pitch = Math.max(-1.2, Math.min(1.2, this.pitch - input.mouseDY * 0.0025));

    // Mouse yaw rotates forward around the (current) up.
    if (input.mouseDX) {
      const yawQ = this._tmp.q.setFromAxisAngle(this.up, -input.mouseDX * 0.0025);
      this.forward.applyQuaternion(yawQ);
    }

    this.crouching = !!input.crouch;
    const wantSprint = !!input.sprint && !this.crouching && this.stamina > 0.05;
    this.sprinting = wantSprint && (input.forward !== 0 || input.right !== 0);

    let speed = this.walkSpeed;
    if (this.sprinting) speed = this.sprintSpeed;
    else if (this.crouching) speed = this.crouchSpeed;

    const fw = input.forward, rt = input.right;
    const len = Math.hypot(fw, rt);
    this.moving = len > 0.01;

    if (this.moving) {
      const nfw = fw / Math.max(1, len);
      const nrt = rt / Math.max(1, len);
      const step = this._tmp.v.set(0, 0, 0)
        .addScaledVector(this.forward, nfw * speed * dt)
        .addScaledVector(this.right, nrt * speed * dt);
      const next = new THREE.Vector3().copy(this.dir).multiplyScalar(this.planet.sampleRadius(this.dir)).add(step);
      const newDir = next.clone().normalize();

      // Collision check (unchanged)
      let blocked = false;
      const proposedPos = newDir.clone().multiplyScalar(this.planet.sampleRadius(newDir));
      for (const p of props) {
        const cr = p.userData.collideRadius;
        if (!cr) continue;
        const d = proposedPos.distanceTo(p.position);
        if (d < cr + this.radius) { blocked = true; break; }
      }
      if (!blocked) {
        // Parallel-transport: rotate the basis by the same rotation that moves
        // dir → newDir. This preserves the orientation of forward relative to
        // the path, eliminating the "rolling" artifact.
        const transport = new THREE.Quaternion().setFromUnitVectors(this.dir, newDir);
        this.forward.applyQuaternion(transport);
        this.right.applyQuaternion(transport);
        this.dir.copy(newDir);
        this.distanceTraveled += step.length();
      }
    }

    // Stamina drain/regen
    if (this.sprinting) this.stamina = Math.max(0, this.stamina - dt * 0.35);
    else this.stamina = Math.min(1, this.stamina + dt * (this.crouching ? 0.4 : 0.25));

    this._sync();
  }

  // Footstep heuristic: return the noise level (0..1) of this frame
  noiseLevel() {
    if (!this.moving) return 0;
    if (this.crouching) return 0.12;
    if (this.sprinting) return 1.0;
    return 0.5;
  }

  shouldEmitFootstep() {
    const stride = this.crouching ? 1.6 : this.sprinting ? 1.0 : 1.3;
    if (this.distanceTraveled - this.lastFootstepDistance > stride) {
      this.lastFootstepDistance = this.distanceTraveled;
      return true;
    }
    return false;
  }
}
